import React, { useState } from "react";
import { Container, Tab, Nav, Row, Col, Card } from "react-bootstrap";
import styles from "./style.module.css";
import {
  FaShieldAlt,
  FaInfoCircle,
  FaListAlt,
  FaBullhorn,
  FaGavel,
  FaGraduationCap,
} from "react-icons/fa";
import TopNavbar from "../navbar/TopNavbar";
import Footer from "../footer/Footer";

// Sample data
const initiatives = [
  {
    id: 1,
    title: "Right to Safety",
    description: "Protection against hazardous goods and services.",
    icon: <FaShieldAlt size={48} color="#007bff" />,
    projects: [
      { title: "Consumer Product Testing", summary: "Ensure safety through regular inspections." },
      { title: "Hazard Alert App", summary: "Notify consumers about unsafe goods." },
    ],
    reports: [
      { title: "Annual Product Safety Report", summary: "Findings on unsafe products." },
    ],
    articles: [
      { title: "The Rise of Safe Design", summary: "How industries are adapting to safety standards." },
    ],
    faculty: [
      { name: "Dr. Meera Joshi", summary: "Expert in public safety regulations." },
    ],
    partners: [
      { name: "Safety Bureau of India", summary: "Government agency promoting product safety." },
    ],
  },
  {
    id: 2,
    title: "Right to be Informed",
    description: "Access to accurate product/service information.",
    icon: <FaInfoCircle size={48} color="#007bff" />,
    projects: [
      { title: "Label Check Portal", summary: "Verify information on product labels." },
    ],
    reports: [
      { title: "Information Access Survey 2024", summary: "Consumer awareness statistics." },
    ],
    articles: [
      { title: "Transparency in Advertising", summary: "A deep dive into marketing ethics." },
    ],
    faculty: [
      { name: "Prof. Suresh Patel", summary: "Specialist in consumer rights education." },
    ],
    partners: [
      { name: "Consumer Info Trust", summary: "NGO advocating for better transparency." },
    ],
  },
  {
    id: 3,
    title: "Right to Choose",
    description: "Availability of product/service options.",
    icon: <FaListAlt size={48} color="#007bff" />,
    projects: [
      { title: "Marketplace Freedom Project", summary: "Support diverse product offerings." },
    ],
    reports: [
      { title: "Choice Limitation Audit", summary: "Retail and e-commerce industry analysis." },
    ],
    articles: [
      { title: "Monopoly vs. Consumer Choice", summary: "How competition helps consumers." },
    ],
    faculty: [
      { name: "Dr. Anjali Rao", summary: "Researcher in competitive consumer markets." },
    ],
    partners: [
      { name: "Open Market Foundation", summary: "Promotes consumer choice initiatives." },
    ],
  },
  {
    id: 4,
    title: "Right to be Heard",
    description: "Consumer voices should be acknowledged.",
    icon: <FaBullhorn size={48} color="#007bff" />,
    projects: [
      { title: "VoiceUp App", summary: "Platform for consumer complaints." },
    ],
    reports: [
      { title: "Complaint Redressal Effectiveness", summary: "Government vs private channels." },
    ],
    articles: [
      { title: "Amplifying the Consumer", summary: "The value of feedback systems." },
    ],
    faculty: [
      { name: "Prof. Ravi Khanna", summary: "Expert in customer service systems." },
    ],
    partners: [
      { name: "Consumer Grievance Forum", summary: "Ensures consumer voices are addressed." },
    ],
  },
  {
    id: 5,
    title: "Right to Redress",
    description: "Right to fair settlement of claims.",
    icon: <FaGavel size={48} color="#007bff" />,
    projects: [
      { title: "eRedress Portal", summary: "Submit and track complaints online." },
    ],
    reports: [
      { title: "Redressal Timeliness Report", summary: "Average time for resolution by sector." },
    ],
    articles: [
      { title: "Why Redress Matters", summary: "Stories of successful complaint resolution." },
    ],
    faculty: [
      { name: "Dr. Neelam Yadav", summary: "Legal scholar in consumer protection." },
    ],
    partners: [
      { name: "National Consumer Court", summary: "India’s top forum for redressal." },
    ],
  },
  {
    id: 6,
    title: "Right to Consumer Education",
    description: "Access to knowledge for informed decisions.",
    icon: <FaGraduationCap size={48} color="#007bff" />,
    projects: [
      { title: "Consumer Literacy Drive", summary: "Workshops in schools and communities." },
    ],
    reports: [
      { title: "Awareness Index 2024", summary: "Measure of consumer knowledge growth." },
    ],
    articles: [
      { title: "Learning to Buy Smart", summary: "Tips and tools for informed purchases." },
    ],
    faculty: [
      { name: "Prof. Kavita Das", summary: "Leads outreach for consumer education." },
    ],
    partners: [
      { name: "Education for All Foundation", summary: "Supports training and learning." },
    ],
  },
];

export default function InitiativeScreen() {
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [activeTab, setActiveTab] = useState("projects");

  return (
    <>
    <TopNavbar/>
    <br></br><br></br><br></br>
    <Container className="my-5">
      <h2 className="text-center mb-4">Consumer Rights Initiatives</h2>

      {/* Initiative cards */}
      <Row className="mb-4">
        {initiatives.map((item) => (
          <Col key={item.id} sm={12} md={4} className="mb-3">
            <Card
              className={`h-100 shadow-sm ${styles.card} ${
                selectedInitiative?.id === item.id ? styles.activeCard : ""
              }`}
              onClick={() => {
                setSelectedInitiative(item);
                setActiveTab("projects");
              }}
            >
              <Card.Body className="text-center">
                <div className="mb-3">{item.icon}</div>
                <Card.Title>{item.title}</Card.Title>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Show description + tabs only when initiative is selected */}
      {selectedInitiative && (
        <div className="mt-4">
          <h3>{selectedInitiative.title}</h3>
          <p>{selectedInitiative.description}</p>

          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Nav variant="tabs" className="justify-content-center mb-3">
              <Nav.Item>
                <Nav.Link eventKey="projects">Projects</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="reports">Reports</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="articles">Articles</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="faculty">Faculty Engaged</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="partners">Partners</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content className="border rounded p-4 bg-light">
              <Tab.Pane eventKey="projects">
  <h5>Projects for {selectedInitiative.title}</h5>
  {selectedInitiative.projects?.map((project, index) => (
    <div key={index} className="mb-3">
      <strong>{project.title}</strong>
      <p>{project.summary}</p>
      <button className="btn btn-outline-primary btn-sm">Read More</button>
      <hr />
    </div>
  ))}
</Tab.Pane>
             <Tab.Pane eventKey="reports">
  <h5>Reports for {selectedInitiative.title}</h5>
  {selectedInitiative.reports?.map((report, index) => (
    <div key={index} className="mb-3">
      <strong>{report.title}</strong>
      <p>{report.summary}</p>
      <button className="btn btn-outline-primary btn-sm">Read More</button>
      <hr />
    </div>
  ))}
</Tab.Pane>
              <Tab.Pane eventKey="articles">
  <h5>Articles on {selectedInitiative.title}</h5>
  {selectedInitiative.articles?.map((article, index) => (
    <div key={index} className="mb-3">
      <strong>{article.title}</strong>
      <p>{article.summary}</p>
      <button className="btn btn-outline-primary btn-sm">Read More</button>
      <hr />
    </div>
  ))}
</Tab.Pane>
           <Tab.Pane eventKey="faculty">
  <h5>Faculty Engaged in {selectedInitiative.title}</h5>
  {selectedInitiative.faculty?.map((fac, index) => (
    <div key={index} className="mb-3">
      <strong>{fac.name}</strong>
      <p>{fac.summary}</p>
      <button className="btn btn-outline-primary btn-sm">Read More</button>
      <hr />
    </div>
  ))}
</Tab.Pane>

<Tab.Pane eventKey="partners">
  <h5>Partners of {selectedInitiative.title}</h5>
  {selectedInitiative.partners?.map((partner, index) => (
    <div key={index} className="mb-3">
      <strong>{partner.name}</strong>
      <p>{partner.summary}</p>
      <button className="btn btn-outline-primary btn-sm">Read More</button>
      <hr />
    </div>
  ))}
</Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      )}
    </Container>
    <Footer/>
    </>
  );
}
